import React from 'react';
import {
	Box,
	Chip,
	Step,
	StepLabel,
	Stepper,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { StageDefinition, TOW_BRANCH } from '../../flow/stages';

interface FlowStepperRendererProps {
	pipeline: StageDefinition[];
	currentIndex: number; // -1 if on a branch (e.g. towInfo)
	currentBranchId?: string | null; // e.g. 'towInfo' if currently on the branch
	showTowBranch: boolean;
}

const FlowStepperRenderer: React.FC<FlowStepperRendererProps> = ({
	pipeline,
	currentIndex,
	currentBranchId,
	showTowBranch,
}) => {
	const theme = useTheme();
	const isCompact = useMediaQuery(theme.breakpoints.down('sm'));

	const onTowBranch = currentBranchId === TOW_BRANCH.id;
	// When on the tow branch, mark all linear steps as completed.
	const activeStep = onTowBranch ? pipeline.length : currentIndex;

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				width: '100%',
				flexWrap: 'nowrap',
				overflow: 'hidden',
			}}
		>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Stepper
					alternativeLabel={!isCompact}
					activeStep={activeStep}
					sx={{
						'& .MuiStepLabel-label': {
							fontSize: isCompact ? '0.7rem' : '0.75rem',
							mt: isCompact ? 0 : 0.5,
						},
					}}
				>
					{pipeline.map((stage, idx) => {
						const Icon = stage.icon;
						const label = isCompact ? stage.shortLabel : stage.label;
						return (
							<Step key={stage.id} completed={idx < activeStep}>
								<StepLabel
									StepIconComponent={() => (
										<Box
											sx={{
												width: 24,
												height: 24,
												borderRadius: '50%',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												bgcolor:
													idx === activeStep
														? 'primary.main'
														: idx < activeStep
														? 'success.main'
														: 'action.disabledBackground',
												color:
													idx === activeStep ||
													idx < activeStep
														? 'common.white'
														: 'text.disabled',
											}}
										>
											<Icon
												{...({
													fontSize: 'small',
												} as any)}
											/>
										</Box>
									)}
								>
									{label}
								</StepLabel>
							</Step>
						);
					})}
				</Stepper>
			</Box>

			{showTowBranch && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
						flexShrink: 0,
						opacity: onTowBranch ? 1 : 0.55,
					}}
				>
					<SubdirectoryArrowRightIcon
						fontSize="small"
						sx={{ color: 'text.secondary' }}
					/>
					<Chip
						label={isCompact ? TOW_BRANCH.shortLabel : TOW_BRANCH.label}
						size="small"
						color={onTowBranch ? 'primary' : 'default'}
						variant={onTowBranch ? 'filled' : 'outlined'}
					/>
				</Box>
			)}
		</Box>
	);
};

export default FlowStepperRenderer;
